import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { DEFAULT_LOCALE, isSupportedLocale, type AppLocale } from '@shared/i18n/locales'

export type Theme = 'light' | 'dark' | 'system'

const PREFS_KEY = 'ifs.ui.prefs'

interface Prefs {
  locale: AppLocale
  theme: Theme
}

// Only locale and theme are persisted: never PII, tokens or KYC data (spec §7, §12).
function readPrefs(): Partial<Prefs> {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    const parsed = raw ? (JSON.parse(raw) as Partial<Prefs>) : {}
    return {
      locale: parsed.locale && isSupportedLocale(parsed.locale) ? parsed.locale : undefined,
      theme: parsed.theme,
    }
  } catch {
    return {}
  }
}

export const useUiStore = defineStore('ui', () => {
  const prefs = readPrefs()
  const locale = ref<AppLocale>(prefs.locale ?? DEFAULT_LOCALE)
  const theme = ref<Theme>(prefs.theme ?? 'system')
  const sidebarOpen = ref(false)
  const sidebarCollapsed = ref(false)

  watch([locale, theme], () => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({ locale: locale.value, theme: theme.value }))
    } catch {
      // storage unavailable (private mode): preferences fall back to defaults
    }
  })

  function setLocale(next: AppLocale) {
    locale.value = next
  }
  function setTheme(next: Theme) {
    theme.value = next
  }
  function toggleSidebar(open = !sidebarOpen.value) {
    sidebarOpen.value = open
  }

  // Keep preferences across logout; only transient UI state resets.
  function $reset() {
    sidebarOpen.value = false
  }

  return {
    locale,
    theme,
    sidebarOpen,
    sidebarCollapsed,
    setLocale,
    setTheme,
    toggleSidebar,
    $reset,
  }
})
